import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  CATEGORY_COLORS,
  categoryColor,
  formatCad,
  groupServiceVariants,
  serviceMatches,
  serviceVariantName,
  sortVariants,
  variantLabel,
  type CategoryWithServices,
  type Product,
  type Service,
  type ServiceCategory,
  type ServiceVariantGroup,
} from "@shared/modules/x-atelier";
import { ApiError } from "../../../core/lib/api";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { PageHeader } from "../../../core/components/ui/page-header";
import {
  createCategory,
  createService,
  deleteCategory,
  deleteService,
  getCatalogue,
  getServiceProducts,
  listProducts,
  orderCategories,
  orderServices,
  setServiceLinks,
  setServiceProducts,
  updateCategory,
  updateService,
  type ServiceInput,
} from "../lib/api";
import { formatDuration } from "../lib/dates";
import { MarginBadge, formatPct, marginVariant } from "../components/cost-badge";
import { DiscountRulesCard } from "../components/discount-rules";
import { ServiceEditor, type ServicePlan } from "../components/service-editor";
import { VariantStrip, priceLabel } from "../components/variant-chips";

const CATEGORY_FORM = "atelier-category-form";

interface CategoryDraft {
  name: string;
  onlineVisible: boolean;
  /** Couleur d'agenda ; vide = couleur par défaut du rang. */
  color: string;
}

/**
 * Description d'un service. Les lignes qui ne sont qu'une adresse — le lien
 * vers la procédure interne — deviennent un lien court : répétée sur chaque
 * variante, l'adresse complète noyait le texte utile.
 */
function ServiceDescription({ text }: { text: string }) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return (
    <span className="catalogue__description">
      {lines.map((line, i) => {
        const url = /^\s*(?:(?:proc[ée]dure|lien)\s*:\s*)?(https?:\/\/\S+)\s*$/i.exec(line);
        return url ? (
          <a
            key={i}
            className="catalogue__procedure"
            href={url[1]}
            target="_blank"
            rel="noopener noreferrer"
          >
            Procédure
          </a>
        ) : (
          <span key={i} className="catalogue__description-line">
            {line}
          </span>
        );
      })}
    </span>
  );
}

/** Description commune à toutes les variantes, sinon la première renseignée. */
function groupDescription(variants: Service[]): string | null {
  const described = variants.map((v) => v.description?.trim()).filter(Boolean) as string[];
  return described[0] ?? null;
}

/** Échange deux éléments voisins d'une liste ; renvoie la liste inchangée si le déplacement est impossible. */
function swap<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const target = index + dir;
  if (target < 0 || target >= list.length) return list;
  const out = [...list];
  [out[index], out[target]] = [out[target]!, out[index]!];
  return out;
}

/** « 35 min – 1 h » ; une seule valeur si toutes égales ; « — » si aucune. */
function rangeLabel<T>(values: T[], format: (v: T) => string, compare: (v: T) => number): string {
  if (values.length === 0) return "—";
  const sorted = [...values].sort((a, b) => compare(a) - compare(b));
  const low = sorted[0]!;
  const high = sorted[sorted.length - 1]!;
  return compare(low) === compare(high) ? format(low) : `${format(low)} – ${format(high)}`;
}

/** Prix d'un groupe : « 10,00 $ – 40,00 $ » ; « Sur devis » si aucune déclinaison n'a de prix. */
function priceRange(variants: Service[]): string {
  const prices = variants
    .filter((v) => !v.quoteOnly && v.priceCents !== null)
    .map((v) => v.priceCents as number);
  return prices.length === 0 ? "Sur devis" : rangeLabel(prices, formatCad, (x) => x);
}

/** Coût de revient d'un groupe : une valeur ou une fourchette ; « À préciser » sans coût. */
function costLabel(variants: Service[]): string {
  const costs = variants
    .map((v) => v.costCents)
    .filter((x): x is number => x !== null && x !== undefined);
  return costs.length === 0 ? "À préciser" : rangeLabel(costs, formatCad, (x) => x);
}

/** Oui / Non, ou « Partiel » quand les déclinaisons diffèrent. */
function yesNo(variants: Service[], pick: (s: Service) => boolean): string {
  const yes = variants.filter(pick).length;
  return yes === variants.length ? "Oui" : yes === 0 ? "Non" : "Partiel";
}

function ActiveBadge({ variants }: { variants: Service[] }) {
  const active = variants.filter((v) => v.active).length;
  if (active === variants.length) return <Badge variant="success">Actif</Badge>;
  if (active === 0) return <Badge variant="neutral">Inactif</Badge>;
  return (
    <Badge variant="warning">
      {active}/{variants.length} actives
    </Badge>
  );
}

/** Marge d'un groupe : un badge si toutes égales, sinon la fourchette colorée par la plus basse. */
function MarginCell({ variants }: { variants: Service[] }) {
  const values = variants
    .map((v) => v.marginPct)
    .filter((p): p is number => p !== null && p !== undefined);
  if (values.length === 0) return <MarginBadge pct={null} />;
  const low = Math.min(...values);
  const high = Math.max(...values);
  if (low === high) return <MarginBadge pct={low} />;
  return (
    <Badge variant={marginVariant(low)}>
      {formatPct(low)} – {formatPct(high)}
    </Badge>
  );
}

/** Catalogue de services par catégorie ; édition, réordonnancement et création réservés aux admins. */
export function CataloguePage() {
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [categories, setCategories] = useState<CategoryWithServices[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [categoryModal, setCategoryModal] = useState<{ open: boolean; category?: ServiceCategory }>(
    { open: false },
  );
  const [categoryDraft, setCategoryDraft] = useState<CategoryDraft>({
    name: "",
    onlineVisible: true,
    color: "",
  });
  // Recherche : les catégories sans service correspondant disparaissent.
  const [query, setQuery] = useState("");
  const [editor, setEditor] = useState<{
    open: boolean;
    categoryId: string;
    group?: ServiceVariantGroup;
    variant?: Service;
  }>({ open: false, categoryId: "" });
  // Produits (admin) : liste pour l'éditeur.
  const [products, setProducts] = useState<Product[]>([]);
  const [confirm, setConfirm] = useState<
    | { kind: "category"; item: ServiceCategory }
    | { kind: "group"; item: ServiceVariantGroup }
    | null
  >(null);

  const load = useCallback(() => {
    getCatalogue()
      .then((list) => {
        setCategories(list);
        setFailed(false);
      })
      .catch(() => {
        setCategories([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    listProducts()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, [isAdmin]);

  // Catégories affichées : toutes, ou seulement ce que la recherche retient.
  // Pendant une recherche, monter/descendre porterait sur une liste partielle.
  const sorting = query.trim().length > 0;
  const shown = query.trim()
    ? categories
        .map((c) => ({
          ...c,
          services: c.services.filter((s) => serviceMatches(s, query, c.name)),
        }))
        .filter((c) => c.services.length > 0)
    : categories;

  const run = async (
    work: () => Promise<unknown>,
    success: string,
    failure = "Opération impossible",
  ) => {
    if (busy) return false;
    setBusy(true);
    try {
      await work();
      push("success", success);
      load();
      return true;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      push(
        "danger",
        failure,
        code === "category_not_empty"
          ? "Retirez d'abord les services de cette catégorie."
          : undefined,
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openCategory = (category?: ServiceCategory) => {
    setCategoryDraft({
      name: category?.name ?? "",
      onlineVisible: category?.onlineVisible ?? true,
      color: category?.color ?? "",
    });
    setCategoryModal({ open: true, category });
  };

  const saveCategory = async (e: FormEvent) => {
    e.preventDefault();
    const name = categoryDraft.name.trim();
    if (!name) return;
    const c = categoryModal.category;
    const input = {
      name,
      onlineVisible: categoryDraft.onlineVisible,
      color: categoryDraft.color || null,
    };
    const ok = await run(
      () => (c ? updateCategory(c.id, input) : createCategory(input)),
      c ? "Catégorie mise à jour" : "Catégorie créée",
    );
    if (ok) setCategoryModal({ open: false });
  };

  const openEditor = (categoryId: string, group?: ServiceVariantGroup, variant?: Service) =>
    setEditor({ open: true, categoryId, group, variant });
  const closeEditor = () => setEditor({ open: false, categoryId: "" });

  /**
   * Applique ce que la fenêtre a décidé : un service ou ses déclinaisons à
   * créer, mettre à jour, retirer — puis l'ordre de la catégorie, pour que
   * les déclinaisons restent contiguës et dans l'ordre de la grille.
   */
  const applyPlan = async (plan: ServicePlan) => {
    if (plan.kind === "variant") {
      await updateService(plan.id, plan.input);
      await setServiceProducts(plan.id, plan.products);
      return;
    }
    const existingIds = plan.rows.flatMap((r) => (r.id ? [r.id] : []));
    // Une déclinaison ajoutée à un service existant reprend les produits de la première.
    let template = plan.products;
    if (template === null && plan.rows.some((r) => !r.id) && existingIds[0]) {
      template = (await getServiceProducts(existingIds[0])).map((p) => ({
        productId: p.productId,
        quantity: p.quantity,
      }));
    }
    const orderedIds: string[] = [];
    for (const row of plan.rows) {
      const input: ServiceInput = {
        categoryId: plan.categoryId,
        name: serviceVariantName(plan.shared.name, row.label),
        description: plan.shared.description,
        priceCents: row.priceCents,
        quoteOnly: plan.shared.quoteOnly,
        durationMin: row.durationMin,
        onlineBookable: plan.shared.onlineBookable,
        publicVisible: plan.shared.publicVisible,
        aiPriceVisible: plan.shared.aiPriceVisible,
        laborMin: row.laborMin,
        guideSlugs: plan.shared.guideSlugs,
        bundleServiceIds: plan.shared.bundleServiceIds,
        warrantyEligible: plan.shared.warrantyEligible,
        vehicleClasses: row.vehicleClasses ?? plan.shared.vehicleClasses,
        variantGroup: plan.variantGroup,
        pricingUnit: plan.shared.pricingUnit,
        maxVehicleAgeYears: plan.shared.maxVehicleAgeYears,
        maxDistanceKm: plan.shared.maxDistanceKm,
        ...(plan.shared.active !== undefined ? { active: plan.shared.active } : {}),
      };
      if (row.id) {
        await updateService(row.id, input);
        if (plan.products) await setServiceProducts(row.id, plan.products);
        orderedIds.push(row.id);
      } else {
        const created = await createService({ ...input, active: plan.shared.active ?? true });
        if (template && template.length) await setServiceProducts(created.id, template);
        orderedIds.push(created.id);
      }
    }
    for (const id of plan.deleteIds) await deleteService(id);
    // Liaisons : portées par la famille (toutes les déclinaisons), posées sur la première.
    if (orderedIds[0]) await setServiceLinks(orderedIds[0], plan.shared.links);
    // Ordre : le bloc du service prend la place de sa première déclinaison
    // existante (ou la fin), sans toucher au reste de la catégorie.
    const category = categories.find((c) => c.id === plan.categoryId);
    if (!category) return;
    const deleted = new Set(plan.deleteIds);
    const block = new Set(orderedIds);
    const remaining = category.services
      .map((s) => s.id)
      .filter((id) => !deleted.has(id) && !block.has(id));
    const anchor = category.services.findIndex((s) => block.has(s.id));
    const before =
      anchor < 0
        ? remaining
        : remaining.filter((id) => category.services.findIndex((s) => s.id === id) < anchor);
    const after = anchor < 0 ? [] : remaining.filter((id) => !before.includes(id));
    const next = [...before, ...orderedIds, ...after];
    const movedIn = orderedIds.some((id) => !category.services.some((s) => s.id === id));
    const sameCategory = existingIds.every((id) => category.services.some((s) => s.id === id));
    if (sameCategory || movedIn) await orderServices(next);
  };

  const savePlan = (plan: ServicePlan) =>
    run(
      () => applyPlan(plan),
      plan.kind === "variant"
        ? "Déclinaison mise à jour"
        : editor.group
          ? "Service mis à jour"
          : "Service créé",
      "Enregistrement impossible",
    );

  const moveCategory = (index: number, dir: -1 | 1) => {
    const next = swap(categories, index, dir);
    if (next === categories) return;
    setCategories(next);
    void run(() => orderCategories(next.map((c) => c.id)), "Ordre des catégories enregistré");
  };

  /** Monte ou descend un service avec toutes ses déclinaisons. */
  const moveGroup = (category: CategoryWithServices, index: number, dir: -1 | 1) => {
    const groups = groupServiceVariants(category.services);
    const next = swap(groups, index, dir);
    if (next === groups) return;
    const services = next.flatMap((g) => g.variants);
    setCategories(categories.map((c) => (c.id === category.id ? { ...c, services } : c)));
    void run(() => orderServices(services.map((s) => s.id)), "Ordre des services enregistré");
  };

  const remove = async () => {
    if (!confirm) return;
    const ok =
      confirm.kind === "category"
        ? await run(
            () => deleteCategory(confirm.item.id),
            "Catégorie supprimée",
            "Suppression impossible",
          )
        : await run(
            async () => {
              for (const v of confirm.item.variants) await deleteService(v.id);
            },
            "Service supprimé",
            "Suppression impossible",
          );
    if (ok) setConfirm(null);
  };

  return (
    <div className="page catalogue">
      <PageHeader
        title="Catalogue"
        subtitle="Les services offerts, leur prix par déclinaison, leur durée et ce que le site et l'agent IA peuvent en dire."
        actions={
          isAdmin ? (
            <Button
              variant="primary"
              icon={<Icon name="plus" size={16} />}
              onClick={() => openCategory()}
            >
              Nouvelle catégorie
            </Button>
          ) : undefined
        }
      />

      <div className="atelier-toolbar catalogue__toolbar">
        <div className="atelier-toolbar__search">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un service (nom, description, catégorie)"
            aria-label="Rechercher un service"
            autoComplete="off"
          />
        </div>
        {query ? (
          <Button variant="ghost" size="sm" onClick={() => setQuery("")}>
            Effacer
          </Button>
        ) : null}
      </div>

      {loading ? (
        <p className="empty">Chargement…</p>
      ) : failed ? (
        <p className="empty">Catalogue indisponible — réessayez dans un instant.</p>
      ) : categories.length === 0 ? (
        <p className="empty">
          {isAdmin
            ? "Aucune catégorie : créez-en une pour commencer."
            : "Le catalogue est vide pour l'instant."}
        </p>
      ) : shown.length === 0 ? (
        <p className="empty">Aucun service ne correspond à « {query} ».</p>
      ) : (
        <div className="catalogue__list">
          {query ? null : <DiscountRulesCard categories={categories} isAdmin={isAdmin} />}
          {shown.map((c, index) => {
            const groups = groupServiceVariants(c.services);
            return (
              <details key={c.id} className="form-section catalogue__category" open>
                <summary className="form-section__head">
                  <Chevron />
                  <span
                    className="catalogue__category-dot"
                    style={{ background: categoryColor(c) }}
                    aria-hidden="true"
                  />
                  <span className="form-section__title">{c.name}</span>
                  <span className="form-section__count">
                    — {groups.length} service{groups.length > 1 ? "s" : ""}
                    {c.services.length !== groups.length ? ` · ${c.services.length} tarifs` : ""}
                  </span>
                  {!c.onlineVisible ? <Badge variant="neutral">Hors ligne</Badge> : null}
                </summary>

                {isAdmin ? (
                  <div className="catalogue__category-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Icon name="plus" size={14} />}
                      onClick={() => openEditor(c.id)}
                    >
                      Ajouter un service
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openCategory(c)}>
                      Modifier
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn--icon"
                      aria-label={`Monter ${c.name}`}
                      title="Monter"
                      disabled={busy || sorting || index === 0}
                      icon={<Icon name="arrow-up" size={16} />}
                      onClick={() => moveCategory(index, -1)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn--icon"
                      aria-label={`Descendre ${c.name}`}
                      title="Descendre"
                      disabled={busy || sorting || index === categories.length - 1}
                      icon={<Icon name="arrow-down" size={16} />}
                      onClick={() => moveCategory(index, 1)}
                    />
                    <Button
                      variant="ghost-danger"
                      size="sm"
                      disabled={c.services.length > 0}
                      onClick={() => setConfirm({ kind: "category", item: c })}
                    >
                      Supprimer
                    </Button>
                  </div>
                ) : null}

                {c.services.length === 0 ? (
                  <p className="catalogue__empty">Aucun service dans cette catégorie.</p>
                ) : (
                  <table className="table catalogue__table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th className="catalogue__num">Prix</th>
                        <th>Durée</th>
                        {isAdmin ? <th className="catalogue__num">Coût</th> : null}
                        {isAdmin ? <th>Marge</th> : null}
                        <th className="table__col--wide">En ligne</th>
                        <th className="table__col--wide">Prix IA</th>
                        <th>Actif</th>
                        {isAdmin ? <th /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((group, gi) => {
                        // Un service décliné tient sur une rangée : son nom,
                        // puis une pastille par déclinaison avec son prix.
                        const variants = sortVariants(group.variants);
                        const grouped = variants.length > 1;
                        const single = grouped ? null : variants[0]!;
                        const description = groupDescription(variants);
                        const none = variants.every((v) => !v.active);
                        return (
                          <tr
                            key={group.key}
                            className={[
                              "catalogue__row",
                              none ? "catalogue__row--inactive" : "",
                              grouped ? "catalogue__row--group" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <td className="catalogue__service-cell">
                              <span className="catalogue__service">{group.name}</span>
                              {description ? <ServiceDescription text={description} /> : null}
                              {single && single.bundleServiceIds.length > 1 ? (
                                <span className="catalogue__bundle">
                                  Forfait de {single.bundleServiceIds.length} services — proposé
                                  quand ils sont cédulés séparément
                                </span>
                              ) : null}
                              {grouped ? (
                                // Les déclinaisons sous le nom, dans le même bloc :
                                // même service, un prix par taille — comme une
                                // fiche de boutique en ligne.
                                <VariantStrip
                                  variants={variants}
                                  ariaLabel={`Déclinaisons de ${group.name}`}
                                  onPick={isAdmin ? (v) => openEditor(c.id, group, v) : undefined}
                                  titleFor={
                                    isAdmin
                                      ? (v) =>
                                          `Modifier la déclinaison ${variantLabel(v) || v.name} — ${priceLabel(v)}`
                                      : undefined
                                  }
                                />
                              ) : null}
                            </td>
                            <td className="catalogue__num">
                              {grouped ? (
                                <span className="catalogue__price-range">
                                  {priceRange(variants)}
                                </span>
                              ) : (
                                <span className="catalogue__price">{priceLabel(single!)}</span>
                              )}
                            </td>
                            <td className="catalogue__duration">
                              {rangeLabel(
                                variants.map((v) => v.durationMin),
                                formatDuration,
                                (d) => d ?? Number.POSITIVE_INFINITY,
                              )}
                            </td>
                            {isAdmin ? (
                              <td className="catalogue__num">{costLabel(variants)}</td>
                            ) : null}
                            {isAdmin ? (
                              <td>
                                <MarginCell variants={variants} />
                              </td>
                            ) : null}
                            <td className="table__col--wide">
                              {yesNo(variants, (v) => v.onlineBookable)}
                            </td>
                            <td className="table__col--wide">
                              {yesNo(variants, (v) => v.aiPriceVisible)}
                            </td>
                            <td>
                              <ActiveBadge variants={variants} />
                            </td>
                            {isAdmin ? (
                              <td className="table__actions">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="btn--icon"
                                  aria-label={`Monter ${group.name}`}
                                  title="Monter"
                                  disabled={busy || sorting || gi === 0}
                                  icon={<Icon name="arrow-up" size={16} />}
                                  onClick={() => moveGroup(c, gi, -1)}
                                />{" "}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="btn--icon"
                                  aria-label={`Descendre ${group.name}`}
                                  title="Descendre"
                                  disabled={busy || sorting || gi === groups.length - 1}
                                  icon={<Icon name="arrow-down" size={16} />}
                                  onClick={() => moveGroup(c, gi, 1)}
                                />{" "}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditor(c.id, group)}
                                >
                                  Modifier
                                </Button>{" "}
                                <Button
                                  variant="ghost-danger"
                                  size="sm"
                                  onClick={() => setConfirm({ kind: "group", item: group })}
                                >
                                  Supprimer
                                </Button>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </details>
            );
          })}
        </div>
      )}

      {/* ---- Catégorie ---- */}
      <Modal
        open={categoryModal.open}
        title={categoryModal.category ? "Modifier la catégorie" : "Nouvelle catégorie"}
        onClose={() => setCategoryModal({ open: false })}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setCategoryModal({ open: false })}
              disabled={busy}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              form={CATEGORY_FORM}
              variant="primary"
              disabled={busy || !categoryDraft.name.trim()}
            >
              {categoryModal.category ? "Enregistrer" : "Créer la catégorie"}
            </Button>
          </>
        }
      >
        <form id={CATEGORY_FORM} className="atelier-form" onSubmit={(e) => void saveCategory(e)}>
          <Field label="Nom">
            <Input
              value={categoryDraft.name}
              onChange={(e) => setCategoryDraft({ ...categoryDraft, name: e.target.value })}
              placeholder="ex : Antirouille"
              autoFocus
              required
            />
          </Field>
          <Field
            label="Couleur dans l'agenda"
            hint="Les rendez-vous de cette catégorie prennent cette couleur."
          >
            <div className="color-choice" role="group" aria-label="Couleur de la catégorie">
              <button
                type="button"
                className={`color-choice__auto${categoryDraft.color === "" ? " is-on" : ""}`}
                aria-pressed={categoryDraft.color === ""}
                onClick={() => setCategoryDraft({ ...categoryDraft, color: "" })}
              >
                Par défaut
              </button>
              {CATEGORY_COLORS.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className={`color-choice__swatch${categoryDraft.color === hex ? " is-on" : ""}`}
                  style={{ background: hex }}
                  aria-pressed={categoryDraft.color === hex}
                  aria-label={`Couleur ${hex}`}
                  title={hex}
                  onClick={() => setCategoryDraft({ ...categoryDraft, color: hex })}
                />
              ))}
            </div>
          </Field>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={categoryDraft.onlineVisible}
              onChange={(e) =>
                setCategoryDraft({ ...categoryDraft, onlineVisible: e.target.checked })
              }
            />
            Visible sur le site et réservable en ligne
          </label>
        </form>
      </Modal>

      {/* ---- Service et déclinaisons ---- */}
      <ServiceEditor
        key={`${editor.group?.key ?? "new"}-${editor.variant?.id ?? ""}-${editor.open}`}
        open={editor.open}
        categories={categories}
        products={products}
        categoryId={editor.categoryId}
        group={editor.group}
        variant={editor.variant}
        busy={busy}
        onClose={closeEditor}
        onSave={savePlan}
      />

      <Modal
        open={confirm !== null}
        title={confirm?.kind === "category" ? "Supprimer la catégorie ?" : "Supprimer le service ?"}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          {confirm?.kind === "category"
            ? `« ${confirm.item.name} » sera supprimée.`
            : confirm?.kind === "group"
              ? confirm.item.variants.length > 1
                ? `« ${confirm.item.name} » et ses ${confirm.item.variants.length} déclinaisons seront retirés du catalogue ; les rendez-vous existants gardent leur libellé.`
                : `« ${confirm.item.name} » sera retiré du catalogue ; les rendez-vous existants gardent leur libellé.`
              : ""}
        </p>
      </Modal>
    </div>
  );
}

function Chevron() {
  return (
    <svg
      className="form-section__chevron"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
